/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import {
    blockMainActionVar,
    cancellationDialogDataVar,
    CreateTemplateInstanceWizardSteps,
    currentMaxStepVar,
    currentModeVar,
    currentStepVar,
    formsIframeSavingStatusVar,
    Mode,
    templateInstanceTitleVar,
} from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    currentTeamInCreationVar,
    modalDataVar,
    ModalType,
    TeamBasicInfo,
    TemplateScope,
} from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { Paths } from '../../../common/enums/Paths';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { getTemplateScenarioName, useIsTemplateInstanceChanged } from '../../../utilities/TemplateUtils';
import { stringFormat } from '../../../utilities/Utils';

import {
    CreateTemplateInstanceCommandBarComponent,
    CreateTemplateInstanceCommandBarComponentProps,
} from './CreateTemplateInstanceCommandBar.component';

export const CreateTemplateInstanceCommandBarContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const loc = host.getLocale();
    const history = useHistory();
    const currentScope = useReactiveVar<TemplateScope>(currentScopeInCreationVar);
    const currentTeam = useReactiveVar<TeamBasicInfo>(currentTeamInCreationVar);
    const currentStep = useReactiveVar(currentStepVar);
    const currentStepHasError = useReactiveVar(blockMainActionVar);
    const currentMaxStep = useReactiveVar(currentMaxStepVar);
    const currentMode = useReactiveVar(currentModeVar);
    const title = useReactiveVar(templateInstanceTitleVar);
    const formsIframeSavingStatus = useReactiveVar(formsIframeSavingStatusVar);
    const isTemplateInstanceChanged = useIsTemplateInstanceChanged(currentMode);

    let navigationSubTitle;
    let scopeText = '';

    if (currentMode === Mode.Create) {
        if (currentScope === TemplateScope.Team) {
            scopeText = currentTeam.name;
        } else if (currentScope === TemplateScope.TenantAll) {
            scopeText = loc.strings.org_wide_option_text;
        } else {
            scopeText = loc.strings.specific_people_option_text;
        }

        navigationSubTitle = stringFormat(loc.strings.create_template_instance_navigation_title, { scope: scopeText });
    } else if (currentMode === Mode.Edit) {
        scopeText = title;
        navigationSubTitle = stringFormat(loc.strings.edit_template_instance_navigation_title, {
            templateInstanceTitle: scopeText,
        });
    }

    let showNext = false;
    let showBack = false;
    let showPreview = false;
    let showCancel = false;
    let showSave = false;

    if (currentMode === Mode.Create) {
        if (currentStep === CreateTemplateInstanceWizardSteps.BasicSettings) {
            showCancel = true;
        }

        if (currentStep <= 2) {
            showNext = true;
        }

        if (currentStep >= 2) {
            showBack = true;
        }

        if (currentStep === CreateTemplateInstanceWizardSteps.WorkflowSettings) {
            showPreview = true;
        }
    } else if (currentMode === Mode.Edit) {
        showCancel = true;
        showSave = true;
    }

    const onNext = (): void => {
        const nextStep = currentStep + 1;
        logger.markScenario(getTemplateScenarioName(currentMode), StepName.CarouselNextClicked, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.CarouselNextClicked,
            'click',
            'nav',
            'PrimaryButton',
            'carouselButton',
            PanelRegion.Main,
            PanelType.TemplateManagement,
            {
                targeStep: nextStep,
            }
        );

        currentStepVar(nextStep);
        if (currentStep === currentMaxStep) {
            currentMaxStepVar(nextStep);
        }
    };

    const onBack = (): void => {
        const previousStep = currentStep - 1;
        logger.markScenario(getTemplateScenarioName(currentMode), StepName.CarouselPreviousClicked, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.CarouselPreviousClicked,
            'click',
            'nav',
            'ActionButton',
            'carouselButton',
            PanelRegion.Main,
            PanelType.TemplateManagement,
            {
                targetStep: previousStep,
            }
        );

        currentStepVar(previousStep);
    };

    const onPreview = (): void => {
        logger.markScenario(getTemplateScenarioName(currentMode), StepName.OpenPreviewTemplateInstance, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.OpenPreviewTemplateInstance,
            'click',
            'nav',
            'PrimaryButton',
            'carouselButton',
            PanelRegion.Main,
            PanelType.TemplateManagement
        );

        modalDataVar({ isOpen: true, type: ModalType.PreviewTemplateInstance });
    };

    const navigateBack = (): void => {
        logger.updateCommonProperty({ templateInstanceId: '' });
        logger.markScenarioComplete(getTemplateScenarioName(currentMode));
        logger.markScenario(ScenarioName.TemplateManagement, StepName.ExitTemplateInstanceWizard, StatusName.NA);

        history.replace({
            pathname: Paths.BasePath + Paths.TemplateManagement,
        });
    };

    const commandBarProps: CreateTemplateInstanceCommandBarComponentProps = {
        navigationTitle: loc.strings.template_management_text,
        navigationSubTitle,
        onNavigateBack: (): void => {
            if (isTemplateInstanceChanged) {
                cancellationDialogDataVar({
                    isOpen: true,
                    discardCallback: navigateBack,
                });
            } else {
                navigateBack();
            }
        },
        onNext,
        onBack,
        onPreview,
        showNext,
        disableMainAction: currentStepHasError,
        showBack,
        showPreview,
        showCancel,
        showSave,
        formsIframeSavingStatus,
    };

    return <CreateTemplateInstanceCommandBarComponent {...commandBarProps} />;
};
